import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, collection } from 'firebase/firestore';
import { storage, db } from '../firebase';
import type { DrillShotType } from '../types/lesson';
import { createDrill } from '../services/drills';
import ButtonSpinner from './ButtonSpinner';
import { busyStyle } from '../utils/busyStyle';

interface AddDrillModalProps {
  isOpen: boolean;
  onClose: () => void;
  coachId: string;
  /** Called after the drill is successfully created */
  onDrillCreated: (drillId: string) => void;
}

const SHOT_TYPE_LABELS: Record<DrillShotType, string> = {
  forehand: 'Forehand',
  backhand: 'Backhand',
  serve: 'Serve',
  volley: 'Volley',
  return: 'Return',
  movement: 'Movement',
  general: 'General',
};

const SHOT_TYPE_ORDER: DrillShotType[] = [
  'forehand', 'backhand', 'serve', 'volley', 'return', 'movement', 'general',
];

type Step = 'source' | 'recording' | 'details';

async function uploadDrillVideo(
  coachId: string,
  drillId: string,
  file: Blob,
  ext: string
): Promise<{ url: string; path: string }> {
  const storagePath = `drills/${coachId}/${drillId}.${ext}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { url, path: storagePath };
}

export function AddDrillModal({ isOpen, onClose, coachId, onDrillCreated }: AddDrillModalProps) {
  const [step, setStep] = useState<Step>('source');
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoExt, setVideoExt] = useState<string>('mp4');
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Form state
  const [title, setTitle] = useState('');
  const [shotType, setShotType] = useState<DrillShotType>('forehand');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Camera recording state
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetAll = () => {
    setStep('source');
    setVideoBlob(null);
    setVideoExt('mp4');
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setTitle('');
    setShotType('forehand');
    setDescription('');
    setSaving(false);
    setError('');
    setIsRecording(false);
    stopCameraStream();
  };

  const stopCameraStream = () => {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  // Clean up the preview URL when blob changes
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Stop camera if the modal closes while on the recording step
  useEffect(() => {
    if (!isOpen) {
      stopCameraStream();
    }
  }, [isOpen]);

  // ── Upload from computer ────────────────────────────────────────────────────
  const handleFileSelected = (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file');
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() || 'mp4';
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(file);
    setVideoBlob(file);
    setVideoExt(ext);
    setPreviewUrl(url);
    setStep('details');
    setError('');
  };

  // ── Camera recording ────────────────────────────────────────────────────────
  const startCamera = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      mediaStreamRef.current = stream;
      setStep('recording');
      // Wait for React to render the video element before attaching stream
      setTimeout(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
        }
      }, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not access camera');
    }
  };

  const startRecording = () => {
    if (!mediaStreamRef.current) return;
    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(mediaStreamRef.current, { mimeType: 'video/webm' });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      setVideoBlob(blob);
      setVideoExt('webm');
      setPreviewUrl(url);
      stopCameraStream();
      setStep('details');
    };
    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  // ── Save drill ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!videoBlob || !title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const drillId = doc(collection(db, 'drills')).id;
      const { url, path } = await uploadDrillVideo(coachId, drillId, videoBlob, videoExt);
      const createdId = await createDrill({
        title: title.trim(),
        description: description.trim(),
        shotType,
        videoUrl: url,
        videoStoragePath: path,
        isStock: false,
        coachId,
      });
      onDrillCreated(createdId);
      resetAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save drill');
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#152238] border border-[#2A3F5C] rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-[#2A3F5C]">
              <h2 className="text-base font-bold text-[#F1F5F9]">
                {step === 'source' && 'Add Custom Drill'}
                {step === 'recording' && 'Record Drill'}
                {step === 'details' && 'Drill Details'}
              </h2>
              <button
                type="button"
                onClick={handleClose}
                className="w-7 h-7 rounded-full flex items-center justify-center text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#1A2B42]"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="p-5">
              {error && (
                <div className="mb-4 rounded-lg bg-red-900/20 border border-red-500/30 px-3 py-2 text-xs text-red-300">
                  {error}
                </div>
              )}

              {/* ── Step 1: source selection ── */}
              {step === 'source' && (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-[#1A2B42] rounded-xl p-6 flex flex-col items-center gap-3 text-[#F1F5F9] hover:bg-[#253756] transition-colors cursor-pointer btn-press"
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="text-sm font-semibold">Upload from computer</span>
                    <span className="text-[11px] text-[#8899AA] text-center">Pick a video file from your device</span>
                  </button>
                  <button
                    type="button"
                    onClick={startCamera}
                    className="bg-[#1A2B42] rounded-xl p-6 flex flex-col items-center gap-3 text-[#F1F5F9] hover:bg-[#253756] transition-colors cursor-pointer btn-press"
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M23 7l-7 5 7 5V7z" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    <span className="text-sm font-semibold">Record with camera</span>
                    <span className="text-[11px] text-[#8899AA] text-center">Capture a drill live from your webcam</span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileSelected(f);
                    }}
                  />
                </div>
              )}

              {/* ── Step 2: recording ── */}
              {step === 'recording' && (
                <div className="flex flex-col gap-3">
                  <div className="rounded-lg overflow-hidden bg-black aspect-video">
                    <video
                      ref={cameraVideoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        stopCameraStream();
                        setStep('source');
                      }}
                      className="flex-1 py-2.5 rounded-lg text-sm bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9] btn-press"
                    >
                      Cancel
                    </button>
                    {!isRecording ? (
                      <button
                        type="button"
                        onClick={startRecording}
                        className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-600 text-white flex items-center justify-center gap-2 btn-press"
                      >
                        <span className="w-2.5 h-2.5 rounded-full bg-white" />
                        Record
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopRecording}
                        className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-600 text-white flex items-center justify-center gap-2 btn-press"
                      >
                        <span className="w-2.5 h-2.5 bg-white" />
                        Stop
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Step 3: details ── */}
              {step === 'details' && previewUrl && (
                <div className="flex flex-col gap-3">
                  <div className="rounded-lg overflow-hidden bg-black aspect-video">
                    <video
                      src={previewUrl}
                      controls
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8899AA] mb-1">
                      Title <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Forehand Topspin Drill"
                      className="w-full bg-[#1E3048] border border-[#2A3F5C] rounded-lg px-3 py-2 text-[16px] leading-[21px] text-[#F1F5F9] placeholder:text-[#5C6B75] focus:outline-none focus:border-[#1A6B8A] input-glow"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8899AA] mb-1">
                      Shot type <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={shotType}
                      onChange={(e) => setShotType(e.target.value as DrillShotType)}
                      className="w-full bg-[#1E3048] border border-[#2A3F5C] rounded-lg px-3 py-2 text-sm text-[#F1F5F9] focus:outline-none focus:border-[#1A6B8A]"
                    >
                      {SHOT_TYPE_ORDER.map((st) => (
                        <option key={st} value={st}>
                          {SHOT_TYPE_LABELS[st]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8899AA] mb-1">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="Setup instructions, key points…"
                      className="w-full bg-[#1E3048] border border-[#2A3F5C] rounded-lg px-3 py-2 text-[16px] leading-[21px] text-[#F1F5F9] placeholder:text-[#5C6B75] focus:outline-none focus:border-[#1A6B8A] resize-none input-glow"
                    />
                  </div>

                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (previewUrl) URL.revokeObjectURL(previewUrl);
                        setPreviewUrl('');
                        setVideoBlob(null);
                        setStep('source');
                      }}
                      disabled={saving}
                      className="flex-1 py-2.5 rounded-lg text-sm bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9] disabled:opacity-50 btn-press"
                    >
                      Back
                    </button>
                    <button
                      aria-busy={saving}
                      type="button"
                      onClick={handleSave}
                      disabled={saving || !title.trim()}
                      className="flex-1 py-2.5 rounded-lg bg-[#1A6B8A] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed btn-press"
                      style={busyStyle(saving)}
                    >
                      {saving ? <ButtonSpinner /> : 'Save drill'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
